// @auth-coverage:public — intentionally public; see @security-memory
import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";

/**
 * Public, anonymous read of a published CMS page + its rendered blocks.
 * Uses the publishable key (anon role). RLS already restricts cms_pages
 * to published rows and cms_page_blocks to blocks on published pages.
 */

function publicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export type PublicCmsBlock = {
  id: string;
  block_type: string;
  position: number;
  inline_content: Record<string, any>;
  device_visibility: string[];
  global: null | {
    id: string;
    block_key: string;
    block_type: string;
    content: Record<string, any>;
  };
};

export type PublicCmsPage = {
  id: string;
  slug: string;
  title: string;
  meta_title: string | null;
  meta_description: string | null;
  og_image_url: string | null;
  canonical_url: string | null;
  noindex: boolean;
  layout: string;
  template: string;
  body: Record<string, any>;
  published_at: string | null;
  updated_at: string;
  blocks: PublicCmsBlock[];
};

export const getPublicCmsPage = createServerFn({ method: "GET" })
  .validator((d) =>
    z.object({ slug: z.string().min(1).max(400) }).parse(d),
  )
  .handler(async ({ data }): Promise<PublicCmsPage | null> => {
    const supabase = publicClient();
    const { data: page, error } = await supabase
      .from("cms_pages")
      .select(
        "id,slug,title,meta_title,meta_description,og_image_url,canonical_url,noindex,layout,template,body,published_at,updated_at,status,scheduled_for",
      )
      .eq("slug", data.slug)
      .eq("status", "published")
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!page) return null;
    // Guard against scheduled-in-future rows leaking
    if (page.scheduled_for && new Date(page.scheduled_for) > new Date()) return null;

    const { data: rawBlocks, error: bErr } = await supabase
      .from("cms_page_blocks")
      .select(
        "id,block_type,position,inline_content,device_visibility,block_id,cms_blocks(id,block_key,block_type,content,is_active,archived_at)",
      )
      .eq("page_id", page.id)
      .order("position", { ascending: true });
    if (bErr) throw new Error(bErr.message);

    const blocks: PublicCmsBlock[] = (rawBlocks ?? []).map((b) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const g = (b as any).cms_blocks;
      const global =
        g && g.is_active && !g.archived_at
          ? {
              id: g.id as string,
              block_key: g.block_key as string,
              block_type: g.block_type as string,
              content: (g.content ?? {}) as Record<string, any>,
            }
          : null;
      return {
        id: b.id,
        block_type: b.block_type,
        position: b.position,
        inline_content: (b.inline_content ?? {}) as Record<string, any>,
        device_visibility: b.device_visibility ?? ["desktop", "tablet", "mobile"],
        global,
      };
    });

    return {
      id: page.id,
      slug: page.slug,
      title: page.title,
      meta_title: page.meta_title,
      meta_description: page.meta_description,
      og_image_url: page.og_image_url,
      canonical_url: page.canonical_url,
      noindex: page.noindex,
      layout: page.layout,
      template: page.template,
      body: (page.body ?? {}) as Record<string, any>,
      published_at: page.published_at,
      updated_at: page.updated_at,
      blocks,
    };
  });

export const listPublishedCmsSlugs = createServerFn({ method: "GET" }).handler(
  async (): Promise<{ slug: string; updated_at: string }[]> => {
    const supabase = publicClient();
    const { data, error } = await supabase
      .from("cms_pages")
      .select("slug,updated_at")
      .eq("status", "published")
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  },
);
